import { Avatar, Button, IconButton, Typography } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CloseIcon from '@mui/icons-material/Close';
import { ConnectionItem } from './ConnectionItem';
import { ConnectedConnection } from '../model/UserConnection';
import { displayNameInMessage, nameInitials } from '@xolv-mybrightlink/common';
import React, { useCallback, useState } from 'react';
import { useConnectionDispatch } from './ConnectionDispatchProvider';
import {
  useDisconnectConnection,
  useRequestConnect,
} from '../hook/useConnectionAction';
import { useCurrentUserGuid } from '../../firebase/useCollection';
import { queryPeerProfileInfo } from '../service/PeerProfileService';
import { useMessageStandaloneDisplayConfig } from '../../messages/care-team/message-bar/MessageStandaloneDisplayConfigProvider';
import { CommunityTab } from '../../messages/care-team/message-bar/MessageBarTabView';

type ConnectedConnectionProps = {
  connection: ConnectedConnection;
};

export const ConnectedConnectionItem = ({
  connection,
}: ConnectedConnectionProps) => {
  const connectionDispatch = useConnectionDispatch();
  const [state, setState] = useState(ConnectedItemState.MESSAGE);
  const currentUserGuid = useCurrentUserGuid();
  const removeConnection = useDisconnectConnection(
    connectionDispatch,
    connection,
  );
  const requestConnect = useRequestConnect(connectionDispatch, connection);
  const { displayConfigDispatch } = useMessageStandaloneDisplayConfig();

  const openMessageBox = useCallback(
    async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      event.stopPropagation();
      const peerProfileGuid = connection.userProfileGuid;
      const peerProfileInfo = await queryPeerProfileInfo(peerProfileGuid);
      const peerToPeerChannelGuid =
        currentUserGuid.localeCompare(peerProfileInfo.userGuid) < 0
          ? `${currentUserGuid}-${peerProfileInfo.userGuid}`
          : `${peerProfileInfo.userGuid}-${currentUserGuid}`;
      displayConfigDispatch({
        type: 'START_CONVERSATION',
        payload: {
          activeTab: CommunityTab,
          selectedChannelGuid: peerToPeerChannelGuid,
        },
      });
    },
    [displayConfigDispatch, connection.userProfileGuid, currentUserGuid],
  );
  const messageAction = (
    <>
      <Button
        variant='contained'
        disableElevation
        sx={{ px: 8, py: 1.5, mr: 4, borderRadius: 7.5 }}
        onClick={openMessageBox}
      >
        <Typography>
          <FormattedMessage id='common.message' />
        </Typography>
      </Button>
      <IconButton
        onClick={(event) => {
          event.stopPropagation();
          setState(ConnectedItemState.REMOVE_CONNECTION);
        }}
      >
        <MoreVertIcon sx={{ verticalAlign: 'middle', color: '#898f9b' }} />
      </IconButton>
    </>
  );
  const removeConnectionAction = (
    <>
      <Button
        onClick={removeConnection}
        variant='outlined'
        color='error'
        sx={{ px: 8, py: 2, mr: 4, borderRadius: 7.5 }}
      >
        <Typography sx={{ fontWeight: 500 }}>
          <FormattedMessage id='community.connect.button.disconnect' />
        </Typography>
      </Button>
      <IconButton
        onClick={(event) => {
          event.stopPropagation();
          setState(ConnectedItemState.MESSAGE);
        }}
      >
        <CloseIcon
          sx={{
            verticalAlign: 'middle',
            backgroundColor: '#898f9b',
            color: '#ecf2fb',
            borderRadius: '50%',
          }}
        />
      </IconButton>
    </>
  );
  const requestConnectAction = (
    <Button
      onClick={requestConnect}
      disableElevation
      variant='contained'
      color='primary'
      sx={{ px: 5, py: 1.5, borderRadius: 7.5 }}
    >
      <Typography sx={{ fontWeight: 500 }}>
        <FormattedMessage id='common.connect' />
      </Typography>
    </Button>
  );
  const actionElement = connection.isDisConnect
    ? requestConnectAction
    : state === ConnectedItemState.MESSAGE
    ? messageAction
    : removeConnectionAction;
  const connectionName = (
    <Typography>
      {displayNameInMessage({
        firstName: connection.firstName,
        lastName: connection.lastName,
      })}
    </Typography>
  );
  const connectionAvatar = (
    <Avatar sx={{ width: 40, height: 40, mr: 5, backgroundColor: '#626a7a' }}>
      {nameInitials({
        firstName: connection.firstName,
        lastName: connection.lastName,
      })}
    </Avatar>
  );
  return (
    <ConnectionItem
      actionElement={actionElement}
      connectionAvatar={connectionAvatar}
      connectionName={connectionName}
      connection={connection}
      isHighlighted={connection.highlighted}
    />
  );
};

enum ConnectedItemState {
  MESSAGE,
  REMOVE_CONNECTION,
}
